import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Spin, Button, Descriptions, Tag, Row, Col, Typography, Space, message } from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
} from '@ant-design/icons';
import useSprayPermissions from '../../../hooks/useSprayPermissions';
import LayoutWrapper from '@iso/components/utility/layoutWrapper';
import SprayJobDetailWrapper from './SprayJobDetail.styles';
import SprayJobActionsPanel from './SprayJobActionsPanel';
import SprayJobActionLog from './SprayJobActionLog';
import { sprayService } from '@iso/services';
import { JOB_TYPES } from '@iso/constants/jobTypes';
import { getFormulationLabel } from '@iso/constants/formulationTypes';
import { getSprayJobStatusLabel, getSprayJobTagColor } from '@iso/constants/sprayJobStates';
import { formatSprayJobDisplayName } from '@iso/constants/sprayJobHelpers';

const { Text } = Typography;

/**
 * Format a min/max range without producing "--" or "- -" artefacts when one
 * bound is null and the other is negative. Falls back to a legacy single
 * value if no range is set and a single legacy value is provided.
 *
 *   formatRange(null, 123, 'km/h')        → "≤ 123 km/h"
 *   formatRange(-3.6, null, 'km/h')       → "≥ -3.6 km/h"
 *   formatRange(-3, -12, '°C')            → "-12 – -3 °C"  (auto-swaps bounds)
 *   formatRange(null, null, 'km/h', 15)   → "15 km/h"     (legacy single value)
 *   formatRange(null, null, 'km/h')       → "—"
 */
const formatRange = (min, max, unit, legacy = null) => {
  const hasMin = min !== null && min !== undefined && min !== '';
  const hasMax = max !== null && max !== undefined && max !== '';
  if (hasMin && hasMax) {
    const lo = Math.min(Number(min), Number(max));
    const hi = Math.max(Number(min), Number(max));
    return `${lo} – ${hi} ${unit}`;
  }
  if (hasMin) return `≥ ${min} ${unit}`;
  if (hasMax) return `≤ ${max} ${unit}`;
  if (legacy !== null && legacy !== undefined && legacy !== '') return `${legacy} ${unit}`;
  return '—';
};

/**
 * Format a pause_zone metadata object into a human-readable summary line.
 * @param {Object|null} pz - { letter, bench, row, ... }
 * @returns {string|null}
 */
const formatPauseZone = (pz) => {
  if (!pz) return null;
  const parts = [pz.letter, pz.bench && `Bench ${pz.bench}`, pz.row && `Row ${pz.row}`].filter(Boolean);
  return parts.join(' / ');
};

const SprayJobDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isPrivileged } = useSprayPermissions();
  const [job, setJob] = useState(null);
  const [changeLog, setChangeLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Round-4 rework: every per-status action moved into the Actions
  // panel below; the per-status handlers that used to live here
  // (handleStartSpraying, handlePauseZoneSubmit) are gone with them.

  /**
   * Fetch the job + change log.
   * @param {Object} opts
   * @param {boolean} [opts.silent=false] - When true, don't flip the
   *   page-level loading state. Used by post-action refreshes (e.g.
   *   after Start / Finish / Refill) so the page doesn't blank out
   *   into a Spin every time the operator clicks a row. The initial
   *   page load still goes through the loading state so the user sees
   *   the spinner once before the data arrives.
   */
  // Wrapped in useCallback so the silent-reload arrow we pass to
  // SprayJobActionsPanel is stable across renders (PR #432 review
  // Warnings #3 + #5: avoids re-render amplification + clears the
  // exhaustive-deps gap on the useEffect below).
  const fetchJob = useCallback((opts = {}) => {
    if (!id) return;
    const silent = !!opts.silent;
    if (!silent) {
      setLoading(true);
      setError(false);
    }
    Promise.all([
      sprayService.getDetail(id),
      sprayService.getChangeLog(id),
    ])
      .then(([jobRes, logRes]) => {
        if (jobRes && jobRes.spray_job) {
          setJob(jobRes.spray_job);
        } else if (!silent) {
          // Only flip the page into the error state on the initial
          // load. A silent refetch failure shouldn't blow away the
          // current view — surface a toast and leave the existing
          // state visible so the operator's place isn't lost.
          setError(true);
          message.error('Failed to load spray job details');
        } else {
          message.error('Failed to refresh spray job');
        }
        if (logRes && logRes.change_log) {
          setChangeLog(logRes.change_log);
        }
      })
      .catch(err => {
        console.error('Error fetching spray job:', err);
        if (!silent) {
          setError(true);
          message.error('Failed to load spray job. Please try again.');
        } else {
          message.error('Failed to refresh spray job');
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    fetchJob();
  }, [id, fetchJob]);

  // Stable reference for the post-action silent reload prop — avoids
  // recreating an inline arrow on every render of SprayJobDetail.
  const silentReload = useCallback(() => fetchJob({ silent: true }), [fetchJob]);

  const getJobTypeLabel = (value) => {
    const type = JOB_TYPES.find(t => t.value === value);
    return type ? type.label : value;
  };

  // ---- legacy per-status action handlers removed in round-4 ----
  // handleStartSpraying / handlePauseZoneSubmit and the pauseZoneMode
  // state used to live here; they were mirrored onto the now-removed
  // header workflow buttons. The Actions panel owns all of that now.

  const renderZones = (zones) => {
    if (!zones || !Array.isArray(zones)) return <Text type="secondary">No zones</Text>;

    // Heterogeneous list — refill markers numbered separately from zones.
    let zoneCount = 0;
    let refillCount = 0;
    return zones.map((zone, index) => {
      if (zone && zone.type === 'refill') {
        refillCount += 1;
        return (
          <div
            key={`refill-${index}`}
            className="zone-card"
            style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}
          >
            <Text strong>Refill {refillCount}</Text>
            <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
              <Col span={24}>
                <Text type="secondary">Liters: </Text>
                <Text>{zone.liters != null ? `${zone.liters} L` : '-'}</Text>
              </Col>
            </Row>
          </div>
        );
      }
      zoneCount += 1;
      return (
        <div key={index} className="zone-card">
          <Text strong>Zone {zoneCount}</Text>
          <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text type="secondary">Start: </Text>
              <Text>
                {zone.startLetter || '-'}
                {zone.startNumber1 ? ` / Bench ${zone.startNumber1}` : ''}
                {zone.startNumber2 ? ` / Row ${zone.startNumber2}` : ''}
              </Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Finish: </Text>
              <Text>
                {zone.finishNumber2 ? `Row ${zone.finishNumber2}` : '-'}
              </Text>
            </Col>
          </Row>
          {zone.comment && (
            <div className="zone-comment">
              {zone.comment}
            </div>
          )}
        </div>
      );
    });
  };

  const renderChemicals = (chemicals) => {
    if (!chemicals || !Array.isArray(chemicals) || chemicals.length === 0) {
      return <Text type="secondary">No chemicals</Text>;
    }
    // Total tank water (Initial Fill, in litres) — used to convert each
    // per-100L rate into the actual amount that needs to be loaded.
    const water = Number(job?.water_quantity);
    const hasWater = Number.isFinite(water) && water > 0;

    return chemicals.map((chem, index) => {
      const rate = Number(chem.quantity);
      // Stored rate_unit is bare ('g' or 'mL') but the form dropdown
      // labels it as "g / 100L" / "mL / 100L" — the per-100L
      // semantics are implicit. Same convention used by MixRecipe.
      const isPer100L = chem.rate_unit && Number.isFinite(rate);
      const amountValue = isPer100L && hasWater
        ? Math.round((rate * water) / 100 * 100) / 100
        : null;
      return (
        <div key={index} className="chemical-row">
          <Row gutter={[16, 8]} align="middle">
            <Col xs={24} md={8}>
              <Text strong>{chem.asset ? chem.asset.title : `Chemical #${chem.chemical_id}`}</Text>
              {chem.asset?.chemical_batch_number && (
                <Text type="secondary"> - {chem.asset.chemical_batch_number}</Text>
              )}
            </Col>
            <Col xs={24} md={5}>
              <Text type="secondary">Rate: </Text>
              <Text>{chem.quantity} {chem.rate_unit ? `${chem.rate_unit} / 100L` : 'L'}</Text>
            </Col>
            <Col xs={24} md={6}>
              {/* Amount used for the planned tank fill (water_quantity).
                  Calculated from the per-100L rate × Initial Fill so
                  the operator can confirm how much product to load
                  without re-doing the Mix Recipe in their head. */}
              <Text type="secondary">Amount: </Text>
              <Text strong>
                {amountValue != null
                  ? `${amountValue} ${chem.rate_unit || ''}`.trim()
                  : '—'}
              </Text>
            </Col>
            <Col xs={24} md={5}>
              {chem.asset?.formulation_type && (
                <Tag>{getFormulationLabel(chem.asset.formulation_type)}</Tag>
              )}
            </Col>
          </Row>
        </div>
      );
    });
  };

  const renderSession = (session) => {
    if (!session) return <Text type="secondary">No session data recorded</Text>;

    return (
      <>
        {session.leftover_mix_quantity != null && (
          <Descriptions.Item label="Leftover Mix">{session.leftover_mix_quantity} L</Descriptions.Item>
        )}
        {session.incident_or_near_miss_report && (
          <div style={{ marginTop: 12 }}>
            <Text strong>Incident/Near Miss Report:</Text>
            <div style={{ padding: '8px 12px', background: '#fff1f0', borderRadius: 4, marginTop: 4 }}>
              {session.incident_or_near_miss_report}
            </div>
          </div>
        )}
        {session.outstanding_tasks && (
          <div style={{ marginTop: 12 }}>
            <Text strong>Outstanding Tasks:</Text>
            <pre style={{ padding: '8px 12px', background: '#fafafa', borderRadius: 4, marginTop: 4, whiteSpace: 'pre-wrap' }}>
              {typeof session.outstanding_tasks === 'string'
                ? session.outstanding_tasks
                : JSON.stringify(session.outstanding_tasks, null, 2)}
            </pre>
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <LayoutWrapper>
        <div style={{ textAlign: 'center', margin: '40px 0' }}>
          <Spin size="large" />
        </div>
      </LayoutWrapper>
    );
  }

  if (error || !job) {
    return (
      <LayoutWrapper>
        <div style={{ textAlign: 'center', margin: '40px 0' }}>
          <Text>{error ? 'Failed to load spray job.' : 'Spray job not found.'}</Text>
          <br />
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'center' }}>
            {error && (
              <Button onClick={fetchJob}>Retry</Button>
            )}
            <Button onClick={() => history.push('/spray')}>
              Back to Spray Module
            </Button>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <SprayJobDetailWrapper>
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/spray')} />
            <h1>{formatSprayJobDisplayName(job)}</h1>
            <Tag color={getSprayJobTagColor(job.status)}>
              {getSprayJobStatusLabel(job.status)}
            </Tag>
          </div>
          {/* Round-4 unified workflow: per-status workflow buttons
              (Prepare Spray / Start Spraying / Pause / Move to Cleanup
              / Finish Job) used to live here, each routing to its own
              full-page step. They're gone now — the Actions panel
              below owns every transition in place. Edit stays for the
              agronomist's job-config workflow. */}
          <Space wrap>
            {isPrivileged && ['to_be_started', 'prepared'].includes(job.status) && (
              <Button
                icon={<EditOutlined />}
                onClick={() => history.push(`/spray-job/edit/${job.id}`)}
              >
                Edit
              </Button>
            )}
          </Space>
        </div>

        {/* Job Info */}
        <div className="detail-section">
          <h3>Job Information</h3>
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
            <Descriptions.Item label="Job Name">{job.job_name}</Descriptions.Item>
            <Descriptions.Item label="Job Type">{getJobTypeLabel(job.job_type)}</Descriptions.Item>
            <Descriptions.Item label="Order Number">{job.order_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getSprayJobTagColor(job.status)}>{getSprayJobStatusLabel(job.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Start Time">{job.start_time || '-'}</Descriptions.Item>
            <Descriptions.Item label="End Time">{job.end_time || '-'}</Descriptions.Item>
            <Descriptions.Item label="Solution Used">{job.solution_used || '-'}</Descriptions.Item>
            <Descriptions.Item label="Responsible">
              {job.responsible ? (job.responsible.name || `User #${job.responsible_id}`) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created">{job.created_at}</Descriptions.Item>
          </Descriptions>
        </div>

        {/* Unified Actions panel (round-4 rework) — operator's
            step-by-step workflow: Prepare → Initial Fill → per-zone
            Start/Pause/Finish (with weather + spray-task gates at
            each Start) → Refill → Spray cleanup. All previously
            page-level concerns (Mix Recipe, live weather, prep /
            spray / cleanup task lists) now live inside the modal for
            the action they belong to — round-5 (Damien): "manually
            set tasks should show in their respective actions, not in
            the other spots." */}
        <SprayJobActionsPanel
          job={job}
          /* Silent refresh after each action — keeps the existing job /
             changeLog state visible while we re-fetch, instead of
             blanking the page out into the loading spinner every time
             the operator clicks Start / Finish / Refill / etc.
             Stable reference (useCallback) so the panel doesn't
             re-render purely because its prop identity changed. */
          onReload={silentReload}
        />

        {/* Action Log — chronological audit trail backed by the
            existing ActivityHistory rows logSprayJobChange writes for
            every Actions-panel transition. Stakeholder direction
            (Damien, round-5): document the action taken, when, and
            who performed it. */}
        <SprayJobActionLog changeLog={changeLog} />

        {/* Zones (planned) section removed — the Actions panel
            above already lists every zone with its planned coords
            and live progress state. Two side-by-side renderings
            of the same data was redundant (Damien). */}

        {/* Chemicals */}
        <div className="detail-section">
          <h3>Chemicals</h3>
          {renderChemicals(job.chemicals)}
        </div>

        {/* Equipment */}
        <div className="detail-section">
          <h3>Equipment & Settings</h3>
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
            <Descriptions.Item label="Tractor">
              {job.tractor ? job.tractor.title : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Sprayer">
              {job.sprayer ? job.sprayer.title : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Nozzle">
              {job.nozzle ? job.nozzle.title : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Tractor Setting">
              {job.tractor_setting ? `${job.tractor_setting} km/h` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Sprayer Setting">
              {job.sprayer_setting ? `${job.sprayer_setting} PSI` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Water Quantity">
              {job.water_quantity ? `${job.water_quantity} L` : '-'}
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Environmental Limits — the preset min/max set by the
            agronomist on the create form. These are the bounds the
            Zone-Start gate compares the live weather snapshot
            against. */}
        <div className="detail-section">
          <h3>Environmental Limits</h3>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
            Preset min / max values set by the agronomist when this spray job was created.
          </Text>
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
            <Descriptions.Item label="Wind">{formatRange(job.wind_limit_min, job.wind_limit_max, 'km/h', job.wind_limit)}</Descriptions.Item>
            <Descriptions.Item label="Delta T">{formatRange(job.delta_t_limit_min, job.delta_t_limit_max, '°C', job.delta_t_limit)}</Descriptions.Item>
            <Descriptions.Item label="Temperature">{formatRange(job.temperature_min, job.temperature_max, '°C', null)}</Descriptions.Item>
            <Descriptions.Item label="Humidity">{formatRange(job.humidity_limit_min, job.humidity_limit_max, '%', null)}</Descriptions.Item>
            <Descriptions.Item label="Rain">
              {job.rain_limit ? `${job.rain_limit} mm/hr` : '—'}
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* Actual environmental range — aggregated from each zone-
            start log row's captured snapshot (temperature, humidity,
            Stull-derived Delta T). Only shown once the job is
            completed so it reads as a post-job summary. Values turn
            red when either end breaches the corresponding preset. */}
        {job.status === 'completed' && (() => {
          const startRows = (changeLog || []).filter(
            (r) => r.action === 'zone_start' || r.action === 'zone_resume'
          );
          if (startRows.length === 0) return null;
          const aggregate = (key) => {
            const vals = startRows
              .map((r) => r.metadata?.[key])
              .filter((v) => v != null && Number.isFinite(Number(v)))
              .map(Number);
            if (!vals.length) return null;
            // reduce instead of Math.min/max(...vals) so we don't hit
            // the V8 spread-argument cap (~65535) on unbounded log
            // sets — non-issue at current zone counts but robust at
            // any scale (PR #459 / #460 review suggestion 4).
            return vals.reduce(
              (acc, v) => ({
                min: v < acc.min ? v : acc.min,
                max: v > acc.max ? v : acc.max,
              }),
              { min: vals[0], max: vals[0] }
            );
          };
          const actual = {
            temperature: aggregate('temperature'),
            humidity: aggregate('humidity'),
            delta_t: aggregate('delta_t'),
          };
          if (!actual.temperature && !actual.humidity && !actual.delta_t) return null;
          const colorFor = (a, presetMin, presetMax) => {
            if (!a) return undefined;
            const below = presetMin != null && a.min < presetMin;
            const above = presetMax != null && a.max > presetMax;
            return (below || above) ? '#cf1322' : '#3f8600';
          };
          const fmt = (a, unit) => a ? `${a.min} – ${a.max} ${unit}`.trim() : '—';
          const tempColor = colorFor(actual.temperature, job.temperature_min, job.temperature_max);
          const humColor = colorFor(actual.humidity, job.humidity_limit_min, job.humidity_limit_max);
          const dtColor = colorFor(actual.delta_t, job.delta_t_limit_min, job.delta_t_limit_max);
          return (
            <div className="detail-section">
              <h3>Actual Environmental Range</h3>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                Min / max readings captured at the start of each zone. Green = stayed within
                the preset limits above; red = at least one zone started outside the preset.
              </Text>
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
                <Descriptions.Item label="Temperature">
                  <Text style={{ color: tempColor }}>{fmt(actual.temperature, '°C')}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Humidity">
                  <Text style={{ color: humColor }}>{fmt(actual.humidity, '%')}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Delta T">
                  <Text style={{ color: dtColor }}>{fmt(actual.delta_t, '°C')}</Text>
                </Descriptions.Item>
              </Descriptions>
            </div>
          );
        })()}

        {/* PPE Requirements */}
        {job.ppe_requirements && (
          <div className="detail-section">
            <h3>PPE Requirements</h3>
            {/* New format: { preparation: [...], spraying: [...] } */}
            {job.ppe_requirements.preparation || job.ppe_requirements.spraying ? (
              <div>
                {job.ppe_requirements.preparation?.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>Preparation:</Text>
                    <div style={{ marginTop: 4 }}>
                      {job.ppe_requirements.preparation.map((ppe, index) => (
                        <Tag key={`prep-${index}`} style={{ marginBottom: 8 }}>{ppe}</Tag>
                      ))}
                    </div>
                  </div>
                )}
                {job.ppe_requirements.spraying?.length > 0 && (
                  <div>
                    <Text strong>Spraying:</Text>
                    <div style={{ marginTop: 4 }}>
                      {job.ppe_requirements.spraying.map((ppe, index) => (
                        <Tag key={`spray-${index}`} style={{ marginBottom: 8 }}>{ppe}</Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : Array.isArray(job.ppe_requirements) && job.ppe_requirements.length > 0 ? (
              /* Old format: flat array */
              <div>
                {job.ppe_requirements.map((ppe, index) => (
                  <Tag key={index} style={{ marginBottom: 8 }}>{ppe}</Tag>
                ))}
              </div>
            ) : null}
          </div>
        )}

        {/* Tasks */}
        {job.tasks && job.tasks.length > 0 && (
          <div className="detail-section">
            <h3>Tasks</h3>
            {job.tasks.map((task, index) => (
              <div key={index} style={{ padding: '4px 0' }}>
                <Text>{task.task_name}</Text>
                {task.mapping_status && (
                  <Tag style={{ marginLeft: 8 }}>{task.mapping_status}</Tag>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Session Data */}
        {job.latest_session && (
          <div className="detail-section">
            <h3>Session Data</h3>
            {renderSession(job.latest_session)}
          </div>
        )}

        {/* The legacy "Changes" timeline that used to live here is
            gone — the Action Log table above is a superset (full
            user names, every action type formatted, sortable
            columns, zone coords + Delta T). */}
      </SprayJobDetailWrapper>
    </LayoutWrapper>
  );
};

export default SprayJobDetail;
